/**
 * The administration shell: header across the top, sidebar down the left, the
 * current screen in the middle.
 *
 * Every signed-in route renders inside this. It owns no data — the outlet's
 * screen does its own loading — so navigating between sections never tears the
 * frame down and rebuilds it.
 *
 * The frame is exactly one screen tall and the main panel is what scrolls. The
 * header and the sidebar are transparent over the page background, so content
 * scrolling under them would show through.
 */
import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { Header } from './Header';
import { HealthBanner } from './HealthBanner';
import { Sidebar, SIDEBAR_WIDTH } from './Sidebar';
import { useModules } from '../modules/useModules';
import { navigationWith, sectionKeyFor } from './navigation';
import { NORMAL_CONTENT_WIDTH, useContentWidth } from './useContentWidth';
import { GLASS_RADIUS, SHELL_GAP, glass } from '../theme/theme';

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const modules = useModules();
  const contentWidth = useContentWidth();

  return (
    <Box sx={{ display: 'flex', height: '100dvh' }}>
      <Box component="nav" sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Temporary below md. keepMounted so opening it on a phone does not
            re-mount and re-measure the whole list every time. It slides over
            the main panel rather than beside it, so it carries the panel's
            glass instead of the bare background. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            // Wider by the groups' gaps on both sides: detached, they have one
            // on the right as well, and they keep the full sidebar width.
            '& .MuiDrawer-paper': (theme) => ({
              ...glass(theme, 'panel'),
              width: `calc(${SIDEBAR_WIDTH}px + ${theme.spacing(SHELL_GAP * 2)})`,
              boxSizing: 'border-box',
            }),
          }}
        >
          <Sidebar attached={false} onNavigate={() => setMobileOpen(false)} />
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              borderRight: 0,
              bgcolor: 'transparent',
            },
          }}
        >
          <Sidebar attached />
        </Drawer>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Header
          onOpenNavigation={() => setMobileOpen(true)}
          sectionKey={sectionKeyFor(pathname, navigationWith(modules))}
          contentWidth={contentWidth.width}
          onToggleContentWidth={contentWidth.toggle}
        />

        {/* No left margin from md up: the sidebar groups end at this edge, so
            the two read as attached. */}
        <Box
          component="main"
          sx={[
            (theme) => glass(theme, 'panel'),
            {
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              borderRadius: `${GLASS_RADIUS}px`,
              ml: { xs: SHELL_GAP, md: 0 },
              mr: SHELL_GAP,
              mb: SHELL_GAP,
            },
          ]}
        >
          <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 3, pb: 6 }}>
            <Box sx={{ maxWidth: contentWidth.width === 'wide' ? 'none' : NORMAL_CONTENT_WIDTH, mx: 'auto' }}>
              {/* Above the outlet rather than inside a screen: a backend that is
                  gone, or its database, or a half-migrated schema is not this
                  screen's problem, it is every screen's. */}
              <HealthBanner />
              <Outlet />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
